import React, { useCallback, useEffect, useRef, useState } from 'react';

const INTRO_DURATION = 6800;
const LOGO_REVEAL_START = 0.7;
const LOGO_REVEAL_LENGTH = 0.16;

const makeStars = (count) =>
  Array.from({ length: count }, (_, index) => {
    const angle = ((index * 137.508) % 360) * (Math.PI / 180);
    const radius = 0.08 + ((index * 53) % 100) / 100;
    return {
      angle,
      radius,
      speed: 0.55 + ((index * 17) % 70) / 100,
      size: 1 + (index % 3),
      red: index % 5 === 0
    };
  });

const makeEnemies = () => [
  { x: 0.77, y: 0.32, t: 0.16, hit: 0.36, sprite: 0 },
  { x: 0.66, y: 0.52, t: 0.3, hit: 0.5, sprite: 1 },
  { x: 0.83, y: 0.68, t: 0.42, hit: 0.62, sprite: 0 }
];

const loadSprite = (src) => {
  const image = new Image();
  image.src = src;
  return image;
};

const drawShip = (ctx, x, y, scale) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  ctx.fillStyle = '#f8f8f8';
  ctx.fillRect(-16, -8, 42, 16);
  ctx.fillRect(-2, -18, 22, 36);
  ctx.fillRect(18, -6, 20, 12);

  ctx.fillStyle = '#e10600';
  ctx.fillRect(-28, -16, 14, 10);
  ctx.fillRect(-28, 6, 14, 10);
  ctx.fillRect(-36, -8, 12, 16);
  ctx.fillRect(4, -6, 12, 12);

  ctx.fillStyle = '#0a0a0b';
  ctx.fillRect(14, -4, 10, 8);

  ctx.strokeStyle = '#e10600';
  ctx.lineWidth = 3;
  ctx.strokeRect(-18, -10, 46, 20);

  ctx.fillStyle = 'rgba(225, 6, 0, 0.42)';
  ctx.fillRect(-52, -10, 18, 6);
  ctx.fillRect(-60, -3, 26, 6);
  ctx.fillRect(-52, 4, 18, 6);
  ctx.restore();
};

const drawEnemy = (ctx, x, y, scale, opacity) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.globalAlpha = opacity;

  ctx.fillStyle = '#111';
  ctx.fillRect(-28, -16, 56, 32);
  ctx.fillRect(-18, -26, 36, 10);
  ctx.fillRect(-18, 16, 36, 10);
  ctx.fillRect(-38, -6, 10, 12);
  ctx.fillRect(28, -6, 10, 12);

  ctx.fillStyle = '#e10600';
  ctx.fillRect(-18, -6, 12, 12);
  ctx.fillRect(6, -6, 12, 12);
  ctx.fillRect(-6, 8, 12, 6);

  ctx.strokeStyle = '#f8f8f8';
  ctx.lineWidth = 2;
  ctx.strokeRect(-28, -16, 56, 32);
  ctx.strokeRect(-38, -6, 76, 12);
  ctx.restore();
};

const LoadingIntro = ({ onComplete }) => {
  const canvasRef = useRef(null);
  const logoRef = useRef(null);
  const completeRef = useRef(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const finish = useCallback(() => {
    if (completeRef.current) return;
    completeRef.current = true;
    setIsLeaving(true);
    window.setTimeout(() => {
      window.sessionStorage.setItem('gameonIntroSeen', 'true');
      onComplete();
    }, 420);
  }, [onComplete]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      const timeout = window.setTimeout(finish, 700);
      return () => window.clearTimeout(timeout);
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const stars = makeStars(92);
    const enemies = makeEnemies();
    const logo = new Image();
    logo.src = '/images/2026/logo-header-transparent-2026.png';
    logoRef.current = logo;
    const shipSprite = loadSprite('/images/2026/sprites/pixel-ship-player.png');
    const enemySprites = [
      loadSprite('/images/2026/sprites/pixel-enemy-red.png'),
      loadSprite('/images/2026/sprites/pixel-enemy-blue.png')
    ];

    let animationFrame;
    let startTime;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;
    };

    const drawExplosion = (x, y, size, progress) => {
      const alpha = Math.max(0, 1 - progress);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = '#e10600';
      ctx.fillStyle = '#f8f8f8';
      ctx.lineWidth = 3;
      for (let i = 0; i < 10; i += 1) {
        const angle = (Math.PI * 2 * i) / 10;
        const inner = size * progress * 0.35;
        const outer = size * (0.4 + progress);
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(angle) * inner, y + Math.sin(angle) * inner);
        ctx.lineTo(x + Math.cos(angle) * outer, y + Math.sin(angle) * outer);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(x, y, size * 0.18 * (1 + progress), 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#e10600';
      ctx.fillRect(x - size * progress, y - 4, size * progress * 2, 8);
      ctx.fillRect(x - 4, y - size * progress, 8, size * progress * 2);
      ctx.restore();
    };

    const drawPixelPlanet = (x, y, size, alpha) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#120000';
      ctx.fillRect(x - size, y - size * 0.6, size * 2, size * 1.2);
      ctx.fillStyle = '#e10600';
      ctx.fillRect(x - size * 0.64, y - size * 0.38, size * 1.28, 8);
      ctx.fillRect(x - size * 0.88, y + size * 0.12, size * 1.76, 6);
      ctx.strokeStyle = 'rgba(248, 248, 248, 0.55)';
      ctx.lineWidth = 3;
      ctx.strokeRect(x - size, y - size * 0.6, size * 2, size * 1.2);
      ctx.restore();
    };

    const drawSprite = (image, x, y, scale, opacity = 1) => {
      if (!image.complete) return false;
      const baseSize = image.naturalWidth || 128;
      const size = baseSize * scale;
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(
        image,
        Math.round(x - size / 2),
        Math.round(y - size / 2),
        Math.round(size),
        Math.round(size)
      );
      ctx.restore();
      return true;
    };

    const draw = (time) => {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / INTRO_DURATION, 1);
      const width = window.innerWidth;
      const height = window.innerHeight;
      const centerX = width / 2;
      const centerY = height / 2;

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#030303';
      ctx.fillRect(0, 0, width, height);
      drawPixelPlanet(width * 0.84, height * 0.2, Math.max(34, width * 0.032), 0.2);

      const tunnelPulse = 0.8 + Math.sin(progress * Math.PI * 10) * 0.12;
      stars.forEach((star, index) => {
        const travel = (progress * star.speed * 1.9 + star.radius) % 1;
        const depth = Math.pow(travel, 1.8);
        const angle = star.angle + progress * 1.8;
        const x = centerX + Math.cos(angle) * depth * width * 0.78;
        const y = centerY + Math.sin(angle) * depth * height * 0.78;
        const tailX = centerX + Math.cos(angle) * Math.max(0, depth - 0.05) * width * 0.78;
        const tailY = centerY + Math.sin(angle) * Math.max(0, depth - 0.05) * height * 0.78;
        ctx.strokeStyle = star.red ? 'rgba(225,6,0,0.62)' : 'rgba(248,248,248,0.52)';
        ctx.lineWidth = Math.max(1, star.size * 0.7 * tunnelPulse);
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(x, y);
        ctx.stroke();

        if (index % 8 === 0) {
          ctx.fillStyle = 'rgba(225,6,0,0.11)';
          ctx.fillRect(Math.round(x), Math.round(y), 7, 2);
        }
      });

      ctx.strokeStyle = `rgba(225, 6, 0, ${0.1 + progress * 0.1})`;
      ctx.lineWidth = 1;
      for (let ring = 0; ring < 5; ring += 1) {
        const ringProgress = (progress * 1.75 + ring / 5) % 1;
        ctx.beginPath();
        ctx.ellipse(
          centerX,
          centerY,
          ringProgress * width * 0.65,
          ringProgress * height * 0.42,
          progress * 1.5,
          0,
          Math.PI * 2
        );
        ctx.stroke();
      }

      enemies.forEach((enemy) => {
        const visible = progress > enemy.t && progress < enemy.hit + 0.12;
        const hitProgress = (progress - enemy.hit) / 0.12;
        const x = width * enemy.x - progress * width * 0.22;
        const y = height * enemy.y + Math.sin(progress * 12 + enemy.x) * 20;
        if (visible && hitProgress < 0) {
          const enemyScale = (width < 760 ? 0.26 : 0.34) * (0.95 + progress * 0.18);
          if (!drawSprite(enemySprites[enemy.sprite], x, y, enemyScale, 0.95)) {
            drawEnemy(ctx, x, y, 0.55 + progress * 0.18, 0.9);
          }
        }
        if (progress > enemy.hit && hitProgress < 1) {
          drawExplosion(x, y, width < 760 ? 34 : 42, hitProgress);
        }
      });

      const shipX = width * (0.16 + Math.min(progress, 0.72) * 0.22);
      const shipY = height * (0.62 - Math.sin(progress * Math.PI) * 0.2);
      const shipScale = width < 760 ? 0.32 : 0.44;
      if (!drawSprite(shipSprite, shipX, shipY, shipScale, 1)) {
        drawShip(ctx, shipX, shipY, 0.68);
      }

      enemies.forEach((enemy) => {
        if (progress > enemy.hit - 0.055 && progress < enemy.hit + 0.018) {
          const x = width * enemy.x - progress * width * 0.22;
          const y = height * enemy.y;
          ctx.strokeStyle = 'rgba(225, 6, 0, 0.95)';
          ctx.lineWidth = width < 760 ? 4 : 5;
          ctx.shadowColor = '#e10600';
          ctx.shadowBlur = 18;
          ctx.beginPath();
          ctx.moveTo(shipX + (width < 760 ? 18 : 28), shipY);
          ctx.lineTo(x, y);
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
      });

      const logoProgress = Math.max(0, (progress - LOGO_REVEAL_START) / LOGO_REVEAL_LENGTH);
      if (logoProgress > 0) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, logoProgress * 1.35);
        const naturalRatio = logo.naturalWidth && logo.naturalHeight ? logo.naturalWidth / logo.naturalHeight : 2.05;
        const maxLogoWidth = Math.min(width * 0.84, 980);
        const maxLogoHeight = Math.min(height * 0.46, 420);
        const logoWidth = Math.min(maxLogoWidth, maxLogoHeight * naturalRatio);
        const logoHeight = logoWidth / naturalRatio;
        ctx.fillStyle = `rgba(0,0,0,${0.94 * logoProgress})`;
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = `rgba(225,6,0,${0.08 * logoProgress})`;
        ctx.fillRect(centerX - logoWidth * 0.54, centerY - logoHeight * 0.52, logoWidth * 1.08, logoHeight * 1.04);
        if (logo.complete) {
          ctx.shadowColor = '#e10600';
          ctx.shadowBlur = 28 * logoProgress;
          ctx.drawImage(logo, centerX - logoWidth / 2, centerY - logoHeight / 2, logoWidth, logoHeight);
          ctx.shadowBlur = 0;
        }
        ctx.fillStyle = '#e10600';
        ctx.font = '900 14px Poppins, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('SYSTEM ONLINE', centerX, centerY + logoHeight / 2 + 42);
        ctx.restore();
      }

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(draw);
      } else {
        finish();
      }
    };

    resize();
    window.addEventListener('resize', resize);
    animationFrame = window.requestAnimationFrame(draw);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
    };
  }, [finish]);

  return (
    <div className={`loading-intro ${isLeaving ? 'is-leaving' : ''}`} role="status" aria-live="polite">
      <canvas ref={canvasRef} aria-hidden="true" />
      <div className="loading-intro-ui">
        <span>Entering Game On</span>
        <button type="button" onClick={finish}>Skip intro</button>
      </div>
    </div>
  );
};

export default LoadingIntro;
